import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import SafeMarkdown from '../components/SafeMarkdown';
import { useAuth } from '../context/AuthContext';
import { getPublicStoryById } from '../services/contentService';
import { getPublicProfileByUsername } from '../services/profileService';

function PublicStory() {
  const { storyId, username } = useParams();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function loadStory() {
      setLoading(true);
      setError('');
      try {
        const loadedProfile = await getPublicProfileByUsername(username);
        const loadedStory = loadedProfile
          ? await getPublicStoryById(storyId, loadedProfile.ownerId, currentUser?.uid || null)
          : null;
        if (active) {
          setProfile(loadedProfile);
          setStory(loadedStory);
        }
      } catch (err) {
        if (active) {
          setError(err.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadStory();

    return () => {
      active = false;
    };
  }, [currentUser, storyId, username]);

  if (loading) {
    return <div className="surface-card">Loading story...</div>;
  }

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  if (!profile || !story) {
    return (
      <section className="auth-panel">
        <SEO title="Story Not Found" />
        <h1>Story not found</h1>
        <p>This story is private, unpublished, or does not exist.</p>
        <Link className="button" to={`/u/${username}`}>Back to profile</Link>
      </section>
    );
  }

  const profileAccent = profile.theme?.accentColor || '#8a5a34';

  return (
    <>
      <SEO title={story.title} description={story.summary || `A public story by ${profile.displayName}.`} />
      <section className="profile-preview" style={{ '--profile-accent': profileAccent }}>
        <div className="profile-hero">
          <p className="eyebrow">Public story by {profile.displayName}</p>
          <h1>{story.title}</h1>
          {story.summary ? <p>{story.summary}</p> : null}
          <div className="button-row">
            <Link className="button button-secondary" to={`/u/${username}`}>Back to profile</Link>
          </div>
        </div>
        <div className="profile-grid">
          <article className="profile-section public-story-body">
            <SafeMarkdown>{story.content || story.summary}</SafeMarkdown>
          </article>
        </div>
      </section>
    </>
  );
}

export default PublicStory;
